import { standardQuerySerializer } from './serializers';
import { applyQueryConfig, makeCancelable, ServiceHandler } from '../utils';
import type { FlowTokens } from './claims/responses/ProofOfLossResponses';
import { activityTrackingServiceMappings } from './activityTrackingServiceMappings';

const refreshAccessTokenHandler = ServiceHandler({
  url: '/api/auth/v1/access',
  method: 'post',
});
export function refreshAccessToken(refreshToken: any) {
  return refreshAccessTokenHandler({ data: { refreshToken } });
}

const getAgencyLogoSmallHandler = ServiceHandler({
  url: '/api/agency/v1/logo/s/:agencyId',
});
export function getAgencyLogoSmall(agencyId: any) {
  return getAgencyLogoSmallHandler(agencyId);
}

const getAgencyLogoMediumHandler = ServiceHandler({
  url: '/api/agency/v1/logo/m/:agencyId',
});
export function getAgencyLogoMedium(agencyId: any) {
  return getAgencyLogoMediumHandler(agencyId);
}

const postAgencyLogoHandler = ServiceHandler({
  url: '/api/agency/v1/logo/initial/:agencyId',
  method: 'post',
});
export function postAgencyLogo(agencyId: any, isSmall: any, payload: any) {
  return postAgencyLogoHandler(agencyId, {
    params: { isSmall },
    data: payload,
  });
}

const uploadAgencyLogoSmallHandler = ServiceHandler({
  url: '/api/agency/v1/logo',
  method: 'post',
});
export function uploadAgencyLogoSmall(req: any, payload: any) {
  return uploadAgencyLogoSmallHandler({
    params: { xCoordinate: req.xCoordinate, yCoordinate: req.yCoordinate },
    data: payload,
  });
}

const delAgencyLogoSmallHandler = ServiceHandler({
  url: '/api/agency/v1/logo/s/:agencyId',
  method: 'delete',
});
export function delAgencyLogoSmall(agencyId: any) {
  return delAgencyLogoSmallHandler(agencyId);
}

const delAgencyLogoMediumHandler = ServiceHandler({
  url: '/api/agency/v1/logo/m/:agencyId',
  method: 'delete',
});
export function delAgencyLogoMedium(agencyId: any) {
  return delAgencyLogoMediumHandler(agencyId);
}

// url intentionally omitted until SSO integration (preserved from original)
export const checkSSO = makeCancelable(ServiceHandler({ url: '' }));

const postSSOTokenHandler = makeCancelable(
  ServiceHandler({ url: '/api/auth/v1/sso/token', method: 'post' })
);
export const postSSOToken = (
  _params: Record<string, unknown> = {},
  data?: unknown
) => postSSOTokenHandler({ data });

const getQRCodeHandler = ServiceHandler({
  url: '/api/auth/v1/mfa/update',
  method: 'put',
});
export function getQRCode(type: any) {
  return getQRCodeHandler({ params: { mfaType: type } });
}

const verifyQRHandler = ServiceHandler({
  url: '/api/auth/v1/mfa/verify',
  method: 'post',
});
export function verifyQR(payload: any) {
  return verifyQRHandler({ data: payload });
}

const resetMfaHandler = ServiceHandler({
  url: '/api/auth/v1/mfa/reset',
  method: 'post',
});
export function resetMfa(payload: any) {
  return resetMfaHandler({ data: payload });
}

const confirmResetMfaHandler = ServiceHandler({
  url: '/api/auth/v1/validateMFAReset/:resetMFAToken',
  method: 'post',
});
export function confirmResetMfa(resetMFAToken: any) {
  return confirmResetMfaHandler(resetMFAToken);
}

export const resetPasswordByEmail = ServiceHandler({
  url: '/api/auth/v1/passwords/:email/reset',
  method: 'PUT',
});

const sendMfaCodeHandler = ServiceHandler({
  url: '/api/auth/v1/mfa/update',
  method: 'put',
});
export function sendMfaCode(type: any) {
  return sendMfaCodeHandler({ params: { mfaType: type } });
}

const verifyMfaCodeHandler = ServiceHandler({
  url: '/api/auth/v1/mfa/verify',
  method: 'post',
});
export function verifyMfaCode(payload: any) {
  return verifyMfaCodeHandler({ data: payload });
}

const getEnvironmentHandler = ServiceHandler({ url: '/api/auth/v1/env' });
export function getEnvironment() {
  return getEnvironmentHandler();
}

const sendCodeToRetailHandler = ServiceHandler({
  url: '/api/auth/v1/wf/access/:workflowId',
});
export function sendCodeToRetail(workflowId: any) {
  return sendCodeToRetailHandler(workflowId);
}

const getRetailAccessTokenHandler = ServiceHandler({
  url: '/api/auth/v1/wf/token',
  method: 'post',
});
export function getRetailAccessToken(payload: any) {
  return getRetailAccessTokenHandler({ data: payload });
}

export const refreshFlowToken = ServiceHandler<
  { refreshToken: string },
  FlowTokens
>({
  url: '/api/auth/v1/wf/token/refresh',
  method: 'post',
});

const getRedirectUrlHandler = ServiceHandler({
  url: '/api/auth/v1/wf/:workflowId',
});
export function getRedirectUrl(workflowId: any) {
  return getRedirectUrlHandler(workflowId);
}

const resendPhoneCodeHandler = ServiceHandler({
  url: '/api/auth/v1/wf/access/phone/:workflowId',
});
export function resendPhoneCode(workflowId: any) {
  return resendPhoneCodeHandler(workflowId);
}

const getSiteKeyHandler = ServiceHandler({ url: '/api/auth/v1/login/sitekey' });
export function getSiteKey() {
  return getSiteKeyHandler();
}

const apiKeysParamSchema = [
  { path: 'page' },
  { path: 'size' },
  { path: 'search' },
  {
    paramKey: 'sort',
    transform: ({ orderBy, order }: any) => {
      return orderBy ? `${orderBy},${order}` : null;
    },
  },
];
const getApiKeysHandler = ServiceHandler({
  url: 'api/auth/v1/api',
  paramsSerializer: (params) =>
    standardQuerySerializer(applyQueryConfig(apiKeysParamSchema, params)),
});
export const getApiKeys = (params: Record<string, unknown> = {}) =>
  getApiKeysHandler({ params });

const createApiKeyHandler = ServiceHandler({
  url: 'api/auth/v1/api',
  method: 'post',
});
export const createApiKey = (
  _params: Record<string, unknown> = {},
  data?: unknown
) => createApiKeyHandler({ data });

const updateApiKeyHandler = ServiceHandler({
  url: 'api/auth/v1/api/:keyId',
  method: 'put',
});
export const updateApiKey = (
  params: { keyId?: string | number } = {},
  data?: unknown
) => updateApiKeyHandler(params.keyId as string | number, { data });

const fetchActivityTrackingHandler = makeCancelable(
  ServiceHandler({
    url: '/api/apiactivity/v1/search',
    paramsSerializer: (params) =>
      standardQuerySerializer(
        applyQueryConfig(activityTrackingServiceMappings, params)
      ),
  })
);
export const fetchActivityTracking = (params: Record<string, unknown> = {}) =>
  fetchActivityTrackingHandler({ params });

const fetchActivityGraphHandler = makeCancelable(
  ServiceHandler({
    url: '/api/apiactivity/v1/graph',
    paramsSerializer: (params) =>
      standardQuerySerializer(
        applyQueryConfig(activityTrackingServiceMappings, params)
      ),
  })
);
export const fetchActivityGraph = (params: Record<string, unknown> = {}) =>
  fetchActivityGraphHandler({ params });

const fetchActivityStatsHandler = ServiceHandler({
  url: '/api/apiactivity/v1/agg/stats',
});
export const fetchActivityStats = (params: Record<string, unknown> = {}) =>
  fetchActivityStatsHandler({ params });

const regenerateSecretHandler = ServiceHandler({
  url: 'api/auth/v1/api/regenerate/:keyId',
  method: 'put',
});
export const regenerateSecret = ({ keyId }: { keyId: string | number }) =>
  regenerateSecretHandler(keyId);

const deleteApiKeyHandler = ServiceHandler({
  url: 'api/auth/v1/api/:keyId',
  method: 'delete',
});
export const deleteApiKey = ({ keyId }: { keyId: string | number }) =>
  deleteApiKeyHandler(keyId);

export const getPermissions = ServiceHandler({
  url: `/api/auth/v1/permissions/map`,
});

export const createRole = ServiceHandler({
  url: '/api/auth/v1/roles',
  method: 'POST',
});

export const updateRole = ServiceHandler({
  url: '/api/auth/v1/roles/:roleId',
  method: 'PUT',
});

export const deleteRole = ServiceHandler({
  url: '/api/auth/v1/roles/:roleId',
  method: 'DELETE',
});

export const getRoleById = ServiceHandler({
  url: '/api/auth/v1/roles/:roleId',
});
